import React, { useEffect, useRef, useState } from 'react';
import { EMPTY_MARK } from '@/components/ui';
import { appVersionInfo } from '../utils/appVersion';

// AccountMenu - the signed-in user on the navbar (V2-040; DESIGN.md NAV-01, NAV-11).
//
// A circle with the user's initials at the far right of the bar, visible at every width – it never
// moves into the collapsed drawer. Click or tap opens a small menu with who is signed in and
// Log Out. It replaces "Hello, name" and the Logout button, which were cut off or pushed into the
// drawer when the bar ran short of room. One neutral style for every environment colour: white
// circle, dark initials.
//
// V2-037: the menu also shows service area(s), home foreman area, and the app version and build.
// The Message Center icon beside the circle waits for the Message Center page, which this branch
// does not have.

export interface AccountMenuProps {
  name: string;
  lastName?: string | null;
  email?: string | null;
  role?: string | null;
  serviceAreas?: string[];
  homeForemanArea?: string | null;
  onLogout: () => void;
}

const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="tw-mt-1.5 tw-text-sm">
    <dt className="tw-text-xs tw-text-content-secondary">{label}</dt>
    <dd className="tw-mb-0 tw-text-content">{children}</dd>
  </div>
);

function initials(name: string, lastName?: string | null): string {
  const first = name.trim().charAt(0);
  const last = (lastName ?? '').trim().charAt(0);
  return `${first}${last}`.toUpperCase() || '?';
}

export const AccountMenu: React.FC<AccountMenuProps> = ({
  name,
  lastName,
  email,
  role,
  serviceAreas = [],
  homeForemanArea,
  onLogout,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const fullName = [name, lastName].filter(Boolean).join(' ');
  const { version, build } = appVersionInfo();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="wrap-account-menu tw-relative tw-ml-auto tw-shrink-0 tw-pl-2">
      <button
        type="button"
        aria-label={`Account: ${fullName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={fullName}
        onClick={() => setOpen((o) => !o)}
        className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-sm tw-font-semibold tw-text-ink tw-shadow-sm hover:tw-ring-2 hover:tw-ring-white/60 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-white"
      >
        {initials(name, lastName)}
      </button>
      {open && (
        <div
          role="menu"
          className="tw-absolute tw-right-0 tw-top-full tw-z-[1060] tw-mt-2 tw-w-64 tw-max-w-[calc(100vw-1rem)] tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-3 tw-text-content tw-shadow-lg"
        >
          <p className="tw-mb-0 tw-font-semibold">{fullName}</p>
          {email && <p className="tw-mb-0 tw-truncate tw-text-sm tw-text-content-secondary">{email}</p>}
          <dl className="tw-mb-0 tw-mt-2 tw-border-t tw-border-line tw-pt-1">
            {role && <Detail label="Role">{role}</Detail>}
            <Detail label={serviceAreas.length === 1 ? 'Service Area' : 'Service Areas'}>
              {serviceAreas.length > 0 ? serviceAreas.join(', ') : EMPTY_MARK}
            </Detail>
            <Detail label="Home Foreman Area">{homeForemanArea || EMPTY_MARK}</Detail>
            <Detail label="Version">
              {version ?? 'not set'}
              {build && <span className="tw-text-content-secondary">{` · build ${build}`}</span>}
            </Detail>
          </dl>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="tw-mt-3 tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-justify-center tw-rounded tw-border tw-border-line-input tw-bg-surface tw-font-medium tw-text-content hover:tw-bg-surface-hover"
          >
            Log Out
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
